/**
 * use-juliette-active-session — Cherche la session Juliette V2 active la plus recente.
 *
 * Une session est "active" si son status est dans ('open', 'pending', 'notified', 'paused') :
 *  - open : session ad-hoc declenchee par l'agent
 *  - pending : creee par le cron (debrief/briefing) en attente de notif
 *  - notified : push envoyee, agent peut entrer dans /debrief ou /morning-briefing
 *  - paused : agent a cliqué "Plus tard"
 *
 * Renvoie la session la plus recente + son URL de destination selon session_type.
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTeamId } from "@/hooks/use-team-id";

export type JulietteSessionType =
  | "briefing_matin"
  | "debrief_soir"
  | "reactivite"
  | "veille_weekend";

export type JulietteSessionStatus =
  | "open"
  | "pending"
  | "notified"
  | "paused";

export interface ActiveJulietteSession {
  id: string;
  session_type: JulietteSessionType;
  status: JulietteSessionStatus;
  scheduled_at: string | null;
  started_at: string;
  href: string;
}

const ACTIVE_STATUSES: JulietteSessionStatus[] = ["open", "pending", "notified", "paused"];

function urlForSession(sessionType: JulietteSessionType, sessionId: string): string {
  if (sessionType === "briefing_matin") return `/morning-briefing/${sessionId}`;
  return `/debrief/${sessionId}`;
}

export function useJulietteActiveSession() {
  const { teamId } = useTeamId();

  return useQuery<ActiveJulietteSession | null>({
    queryKey: ["juliette-active-session", teamId],
    queryFn: async () => {
      if (!teamId) return null;
      const { data, error } = await supabase
        .from("juliette_sessions")
        .select("id, session_type, status, scheduled_at, started_at")
        .eq("team_id", teamId)
        .in("status", ACTIVE_STATUSES)
        .order("started_at", { ascending: false })
        .limit(1);
      if (error) throw error;
      const row = (data ?? [])[0] as
        | {
            id: string;
            session_type: JulietteSessionType;
            status: JulietteSessionStatus;
            scheduled_at: string | null;
            started_at: string;
          }
        | undefined;
      if (!row) return null;
      return { ...row, href: urlForSession(row.session_type, row.id) };
    },
    enabled: !!teamId,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: true,
  });
}
